/** TestChatPanelWidget — persisted experiment run used by owners to test a draft.
 * Replaces components/settings/agent/TestChatPanel.tsx.
 * Consumes useRunAgentTest; each send starts a single experiment run.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, Send, X, Trash2 } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/lib/cn';
import { useRunAgentTest } from '../../../features/settings/draft-preview/useRunAgentTest';

interface Props {
  agentId: string;
  promptVersionId: string | null;
  configVersionId?: string | null;
  draftSkillIds?: string[] | null;
  draftSkillVersionIds?: string[] | null;
  versionLabel: string;
}

export default function TestChatPanelWidget({
  agentId,
  promptVersionId,
  configVersionId,
  draftSkillIds,
  draftSkillVersionIds,
  versionLabel,
}: Props) {
  const { send, cancel, messages, isStreaming, clearMessages } = useRunAgentTest(agentId);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Clear transcript whenever the draft under test changes.
  useEffect(() => {
    clearMessages();
    setInput('');
  }, [promptVersionId, configVersionId, draftSkillIds, draftSkillVersionIds, clearMessages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput('');
    send({ promptVersionId, configVersionId, draftSkillIds, draftSkillVersionIds, message: text });
  };

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-bold">Experiment run — {versionLabel}</h3>
        <p className="grow text-sm text-muted-foreground">
          Runs one persisted experiment against the selected draft. Experiment runs are hidden from normal chat history.
        </p>
        <Button
          aria-label="Clear transcript"
          onClick={clearMessages}
          disabled={isStreaming || messages.length === 0}
          variant="ghost"
          size="icon-sm"
        >
          <Trash2 />
        </Button>
      </div>

      <div className="flex max-h-[40vh] flex-col gap-2 overflow-y-auto">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Send a message to start a persisted experiment run for this draft.
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={cn(
                'max-w-[80%] rounded-md px-3 py-2 whitespace-pre-wrap',
                m.role === 'user'
                  ? 'self-end bg-primary text-primary-foreground'
                  : m.role === 'error'
                    ? 'self-start bg-destructive/10 text-destructive'
                    : 'self-start bg-muted text-foreground',
              )}
            >
              {m.content || (isStreaming && m.role === 'assistant' ? '…' : '')}
            </div>
          ))
        )}
        {isStreaming && (
          <div className="self-start">
            <Loader2 className="size-4 animate-spin" role="progressbar" aria-label="Agent is thinking" />
          </div>
        )}
      </div>

      <div className="flex items-end gap-2">
        <div className="grow">
          <Textarea
            ref={inputRef}
            aria-label="Message"
            placeholder="Ask the agent something…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isStreaming}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
        </div>
        {isStreaming ? (
          <Button variant="secondary" onClick={cancel}>
            <X />
            Cancel
          </Button>
        ) : (
          <Button onClick={handleSend} disabled={!input.trim()}>
            <Send />
            Send
          </Button>
        )}
      </div>
    </div>
  );
}
